import dayjs from "@punchup/utils/dayjs";
import { getWebAppHostname } from "@punchup/utils/hostname";

import Image from "next/image";
import Link from "next/link";
import PunchupLogoIconBlue from "@punchup/ui-common/resources/punchupLogoIconBlue.svg";
import VenuePageFooterSubscribeForm from "./VenuePageFooterSubscribeForm";
import { VenuePagesRow } from "../../data/venue_pages";

export interface VenuePageFooterLink {
  label: string;
  href: string;
  external?: boolean;
}

export interface VenuePageFooterLinkGroup {
  heading: string;
  links: VenuePageFooterLink[];
}

export interface VenuePageFooterLocation {
  name?: string | null;
  streetAddress?: string | null;
  cityState?: string | null;
  postalCode?: string | null;
}

export interface VenuePageFooterNapInfo {
  locations?: VenuePageFooterLocation[];
  phone?: string | null;
  email?: string | null;
}

export interface VenuePageFooterSubscribeProps {
  venue: VenuePagesRow;
  isUserSubscribed: boolean;
  userEmail?: string;
}

interface Props {
  venueName: string;
  linkGroups?: VenuePageFooterLinkGroup[];
  napInfo?: VenuePageFooterNapInfo;
  subscribe?: VenuePageFooterSubscribeProps;
}

function formatPhoneHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

function formatMapsHref(location: VenuePageFooterLocation): string {
  const query = [
    location.name,
    location.streetAddress,
    location.cityState,
    location.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function hasLocationContent(loc: VenuePageFooterLocation): boolean {
  return !!(loc.streetAddress || loc.cityState || loc.postalCode || loc.name);
}

function LocationAddressLink({
  location,
}: {
  location: VenuePageFooterLocation;
}) {
  const cityLine = [location.cityState, location.postalCode]
    .filter(Boolean)
    .join(" ");
  if (!location.streetAddress && !cityLine) return null;
  return (
    <Link
      href={formatMapsHref(location)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col gap-y-1 hover:underline w-fit"
    >
      {location.streetAddress && <span>{location.streetAddress}</span>}
      {cityLine && <span>{cityLine}</span>}
    </Link>
  );
}

function NapBlock({
  venueName,
  napInfo,
  locations,
  compact,
}: {
  venueName: string;
  napInfo: VenuePageFooterNapInfo;
  locations: VenuePageFooterLocation[];
  compact?: boolean;
}) {
  const isMultiLocation = locations.length > 1;
  const hasContact = !!napInfo.phone || !!napInfo.email;
  const locationsGridClass = compact
    ? "grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5"
    : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5";

  return (
    <address className="not-italic flex flex-col gap-y-4 text-[#4A4A4A]">
      <p className="text-base font-semibold text-[#29333C]">{venueName}</p>
      {isMultiLocation ? (
        <ul className={locationsGridClass}>
          {locations.map((location, idx) => (
            <li
              key={`${location.name ?? "location"}:${idx}`}
              className="flex flex-col gap-y-1.5"
            >
              {location.name && (
                <p className="font-semibold text-[#29333C]">{location.name}</p>
              )}
              <LocationAddressLink location={location} />
            </li>
          ))}
        </ul>
      ) : (
        locations[0] && <LocationAddressLink location={locations[0]} />
      )}
      {hasContact && (
        <div className="flex flex-col gap-y-1.5">
          {napInfo.phone && (
            <Link
              href={formatPhoneHref(napInfo.phone)}
              className="hover:underline w-fit"
            >
              {napInfo.phone}
            </Link>
          )}
          {napInfo.email && (
            <Link
              href={`mailto:${napInfo.email}`}
              className="hover:underline w-fit"
            >
              {napInfo.email}
            </Link>
          )}
        </div>
      )}
    </address>
  );
}

function SubscribeBlock({
  venueName,
  subscribe,
}: {
  venueName: string;
  subscribe: VenuePageFooterSubscribeProps;
}) {
  return (
    <div className="flex flex-col gap-y-3 max-w-md">
      <h2 className="text-base font-semibold text-[#29333C]">
        Join the guest list
      </h2>
      <p className="text-[#4A4A4A]">
        Stay up-to-date on the latest from {venueName}
      </p>
      <VenuePageFooterSubscribeForm
        venue={subscribe.venue}
        isUserSubscribed={subscribe.isUserSubscribed}
        userEmail={subscribe.userEmail}
      />
    </div>
  );
}

export default function VenuePageFooter({
  venueName,
  linkGroups,
  napInfo,
  subscribe,
}: Props) {
  const currentYear = dayjs().format("YYYY");
  const groups = (linkGroups ?? []).filter((g) => g.links.length > 0);
  const hasGroups = groups.length > 0;
  const validLocations = (napInfo?.locations ?? []).filter(hasLocationContent);
  const isMultiLocation = validLocations.length > 1;
  const isTwoLocationsWithSubscribe =
    validLocations.length === 2 && !!subscribe;
  const hasNap =
    validLocations.length > 0 || !!napInfo?.phone || !!napInfo?.email;
  const hasTopSection = hasNap || !!subscribe;
  const showDivider = hasGroups || hasTopSection;

  let topSectionClass: string;
  if (!hasNap || !subscribe) {
    topSectionClass = "flex flex-col gap-10 md:gap-12";
  } else if (isTwoLocationsWithSubscribe) {
    topSectionClass =
      "grid grid-cols-1 lg:grid-cols-3 gap-10 md:gap-12 lg:items-start";
  } else if (isMultiLocation) {
    topSectionClass = "flex flex-col gap-10 md:gap-12";
  } else {
    topSectionClass = "grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12";
  }

  return (
    <footer className="px-4 md:px-8 pt-10 md:pt-12 pb-8 md:pb-10 text-[14px] bg-[#F0F0F0] text-[#868787]">
      <div className="md:px-[120px]">
        {hasTopSection && (
          <div className={`pb-10 md:pb-12 ${topSectionClass}`}>
            {hasNap && napInfo && (
              <div
                className={isTwoLocationsWithSubscribe ? "lg:col-span-2" : ""}
              >
                <NapBlock
                  venueName={venueName}
                  napInfo={napInfo}
                  locations={validLocations}
                  compact={isTwoLocationsWithSubscribe}
                />
              </div>
            )}
            {subscribe && (
              <SubscribeBlock venueName={venueName} subscribe={subscribe} />
            )}
          </div>
        )}
        {hasGroups && (
          <nav
            aria-label="Footer navigation"
            className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8 pb-10 md:pb-12 ${
              hasTopSection ? "border-t border-[#D9D9D9] pt-10 md:pt-12" : ""
            }`}
          >
            {groups.map((group) => (
              <div key={group.heading} className="flex flex-col gap-y-3">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-[#4A4A4A]">
                  {group.heading}
                </h2>
                <ul className="flex flex-col gap-y-2">
                  {group.links.map((link) => (
                    <li key={`${group.heading}:${link.label}:${link.href}`}>
                      <Link
                        href={link.href}
                        {...(link.external
                          ? {
                              target: "_blank",
                              rel: "noopener noreferrer",
                            }
                          : {})}
                        className="text-[#868787] hover:text-[#4A4A4A] hover:underline"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        )}
        <div
          className={`flex flex-col gap-y-3 md:flex-row md:items-center md:justify-between ${
            showDivider ? "border-t border-[#D9D9D9] pt-8 md:pt-8" : ""
          }`}
        >
          <div>
            © {currentYear} {venueName}
          </div>
          <Link
            href={getWebAppHostname()}
            aria-label="Punchup"
            className="inline-flex flex-row items-center gap-2"
          >
            <span>Powered by</span>
            <Image
              src={PunchupLogoIconBlue}
              alt="Punchup"
              width={141.75}
              height={18}
              className="h-[16px] w-auto max-w-full opacity-60"
            />
          </Link>
        </div>
      </div>
    </footer>
  );
}
